import React from 'react';
import { 
  HomeIcon,
  BookmarkIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline';
import { Category, Tag } from '../../types';
import CategoryFilter from '../filters/CategoryFilter';
import TagFilter from '../filters/TagFilter';
import Button from '../common/Button';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  categoriesLoading: boolean;
  selectedCategory: number | null;
  onSelectCategory: (categoryId: number | null) => void;
  tags: Tag[];
  tagsLoading: boolean;
  selectedTags: string[];
  onToggleTag: (tagName: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  categories,
  categoriesLoading,
  selectedCategory,
  onSelectCategory,
  tags,
  tagsLoading,
  selectedTags,
  onToggleTag,
}) => {
  const navigationItems = [
    { name: '홈', icon: HomeIcon, href: '/', current: true },
    { name: '즐겨찾기', icon: BookmarkIcon, href: '/favorites', current: false },
    { name: '통계', icon: ChartBarIcon, href: '/stats', current: false },
    { name: '설정', icon: Cog6ToothIcon, href: '/settings', current: false },
  ];

  return (
    <>
      {/* 모바일 오버레이 */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={onClose}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* 사이드바 */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* 사이드바 헤더 */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">필터</h2>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* 사이드바 컨텐츠 */}
          <div className="flex-1 overflow-y-auto">
            {/* 네비게이션 */}
            <nav className="p-3 border-b border-gray-200">
              <ul className="space-y-1">
                {navigationItems.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                        ${item.current
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }
                      `}
                    >
                      <item.icon
                        className={`
                          mr-3 h-5 w-5 flex-shrink-0
                          ${item.current
                            ? 'text-primary-500'
                            : 'text-gray-400 group-hover:text-gray-500'
                          }
                        `}
                      />
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* 카테고리 필터 */}
            <div className="p-3 border-b border-gray-200">
              <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={onSelectCategory}
                loading={categoriesLoading}
              />
            </div>

            {/* 태그 필터 */}
            <div className="p-3">
              <TagFilter
                tags={tags}
                selectedTags={selectedTags}
                onToggleTag={onToggleTag}
                loading={tagsLoading}
                maxVisible={8}
              />
            </div>
          </div>

          {/* 사이드바 푸터 */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-2">
                총 {categories.reduce((sum, cat) => sum + (cat.prompt_count || 0), 0)}개 프롬프트
              </p>
              
              <Button
                variant="primary"
                size="sm"
                fullWidth
                className="mb-2"
              >
                PRO로 업그레이드
              </Button>
              
              <p className="text-xs text-gray-400">
                무제한 프롬프트 + AI 기능
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;